import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase/firebase';
import { collection, query, orderBy, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth';
import { format } from 'date-fns';
import { 
  FiSearch, FiRefreshCw, FiEye, FiTrash2, FiLogOut, FiUser, 
  FiShield, FiCheckCircle, FiClock, FiXCircle, FiCheck, FiChevronDown
} from 'react-icons/fi';

const STATUS_OPTIONS = [
  { value: 'confirmed', label: 'Confirmed', color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'completed', label: 'Completed', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'in-progress', label: 'In Progress', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  { value: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-800 border-amber-300' },
];

const AdminPanel = () => {
  const navigate = useNavigate();
  const [adminUser, setAdminUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    // Check if admin is logged in
    const isLoggedIn = localStorage.getItem('adminLoggedIn') === 'true';
    const email = localStorage.getItem('adminEmail') || '';
    const role = localStorage.getItem('adminRole') || (email.toLowerCase() === 'admin@bookinindia.com' ? 'super_admin' : 'admin');
    const name = localStorage.getItem('adminName') || (role === 'super_admin' ? 'Super Admin' : 'Admin');

    if (!isLoggedIn) {
      navigate('/sign-in');
      return;
    }

    setAdminUser({ email, role, name });

    // Ensure Firebase client has active auth session if rules require auth
    if (!auth.currentUser) {
      signInAnonymously(auth).catch(() => {});
    }

    fetchBookings();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminName');
    localStorage.removeItem('adminLoginTime');
    navigate('/sign-in');
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const bookingsRef = collection(db, 'bookings');
      const q = query(bookingsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const bookingsList = [];
      querySnapshot.forEach((docSnap) => {
        bookingsList.push({ id: docSnap.id, ...docSnap.data() });
      });
      
      setBookings(bookingsList);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      showToast('Error loading bookings. Please refresh.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    if (!bookingId || !newStatus) return;
    setUpdatingId(bookingId);
    
    const formattedStatus = newStatus.toLowerCase();
    
    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      
      // Update with serverTimestamp
      await updateDoc(bookingRef, {
        status: formattedStatus,
        updatedAt: serverTimestamp()
      });
      
      // Update local state for table
      setBookings(prev => prev.map(b => 
        b.id === bookingId ? { ...b, status: formattedStatus } : b
      ));

      // Update selected booking if modal is open
      if (selectedBooking && selectedBooking.id === bookingId) {
        setSelectedBooking(prev => ({ ...prev, status: formattedStatus }));
      }
      
      showToast(`Status updated to "${formattedStatus.toUpperCase()}"`);
    } catch (error) {
      console.error('Error updating status (trying fallback):', error);
      try {
        const bookingRef = doc(db, 'bookings', bookingId);
        await updateDoc(bookingRef, {
          status: formattedStatus,
          updatedAt: new Date().toISOString()
        });

        setBookings(prev => prev.map(b => 
          b.id === bookingId ? { ...b, status: formattedStatus } : b
        ));

        if (selectedBooking && selectedBooking.id === bookingId) {
          setSelectedBooking(prev => ({ ...prev, status: formattedStatus }));
        }

        showToast(`Status updated to "${formattedStatus.toUpperCase()}"`);
      } catch (err) {
        console.error('Final failure updating status:', err);
        showToast('Failed to update status. Please try again.', 'error');
      }
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteBooking = async (bookingId) => {
    if (adminUser?.role !== 'super_admin') {
      alert('Permission Denied: Only Super Admin can delete bookings.');
      return;
    }

    if (window.confirm('Are you sure you want to permanently delete this booking?')) {
      try {
        await deleteDoc(doc(db, 'bookings', bookingId));
        setBookings(prev => prev.filter(booking => booking.id !== bookingId));
        if (selectedBooking?.id === bookingId) {
          setSelectedBooking(null);
        }
        showToast('Booking deleted successfully!');
      } catch (error) {
        console.error('Error deleting booking:', error);
        showToast('Error deleting booking', 'error');
      }
    }
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    switch(s) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border border-green-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border border-red-300';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border border-blue-300';
      case 'in-progress':
        return 'bg-purple-100 text-purple-800 border border-purple-300';
      case 'pending':
        return 'bg-amber-100 text-amber-800 border border-amber-300';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-300';
    }
  };

  const isSuperAdmin = adminUser?.role === 'super_admin';

  const filteredBookings = bookings.filter(booking => {
    const bStatus = (booking.status || '').toLowerCase();
    if (filterStatus !== 'all' && bStatus !== filterStatus.toLowerCase()) return false;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        booking.bookingId?.toLowerCase().includes(searchLower) ||
        booking.customer?.fullName?.toLowerCase().includes(searchLower) ||
        booking.customer?.mobile?.includes(searchTerm) ||
        booking.customer?.email?.toLowerCase().includes(searchLower) ||
        booking.trip?.from?.toLowerCase().includes(searchLower) ||
        booking.trip?.to?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-100 pb-16">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-xl shadow-2xl text-white font-medium text-sm flex items-center gap-2 animate-bounce ${
          toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        }`}>
          {toast.type === 'error' ? <FiXCircle size={18} /> : <FiCheckCircle size={18} />}
          {toast.message}
        </div>
      )}

      {/* Top Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 font-syne">
                Admin Dashboard
              </h1>
              <span className={`text-xs px-2.5 py-1 rounded-full font-bold uppercase tracking-wider flex items-center gap-1 ${
                isSuperAdmin 
                  ? 'bg-red-100 text-red-700 border border-red-300' 
                  : 'bg-blue-100 text-blue-700 border border-blue-300'
              }`}>
                <FiShield size={12} /> {isSuperAdmin ? 'Super Admin' : 'Admin'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Logged in as <strong className="text-gray-700">{adminUser?.email}</strong>
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchBookings}
              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition flex items-center gap-2"
              title="Refresh bookings"
            >
              <FiRefreshCw className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-xl text-sm font-semibold transition flex items-center gap-2"
            >
              <FiLogOut /> Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        
        {/* Filter & Search Bar */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6 border border-gray-200">
          <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-between">
            {/* Status Tabs */}
            <div className="flex flex-wrap gap-2">
              {['all', 'confirmed', 'completed', 'in-progress', 'cancelled', 'pending'].map((st) => (
                <button
                  key={st}
                  onClick={() => setFilterStatus(st)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition ${
                    filterStatus === st
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {st === 'all' ? 'All' : st}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <FiSearch className="absolute left-3.5 top-3 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search by ID, customer name, mobile, route..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Bookings Table */}
        {loading ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-orange-500 border-t-transparent"></div>
            <p className="mt-3 text-sm font-medium text-gray-600">Loading bookings from database...</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Booking ID
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Trip Details
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Vehicle
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Status & Change
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBookings.map((booking) => (
                    <tr key={booking.id} className="hover:bg-orange-50/40 transition">
                      {/* Booking ID & Date */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-orange-600 font-mono">
                          {booking.bookingId || booking.id?.slice(0, 10)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {booking.createdAt?.toDate 
                            ? format(booking.createdAt.toDate(), 'dd/MM/yyyy hh:mm a')
                            : 'Date N/A'}
                        </div>
                      </td>

                      {/* Customer */}
                      <td className="px-5 py-4">
                        <div className="text-sm font-bold text-gray-900">
                          {booking.customer?.fullName || 'N/A'}
                        </div>
                        <div className="text-xs font-medium text-gray-600 mt-0.5">
                          📞 {booking.customer?.mobile || 'N/A'}
                        </div>
                        {booking.customer?.email && (
                          <div className="text-xs text-gray-400 truncate max-w-[180px]">
                            ✉️ {booking.customer?.email}
                          </div>
                        )}
                      </td>

                      {/* Trip */}
                      <td className="px-5 py-4">
                        <div className="text-sm font-semibold text-gray-900">
                          {booking.trip?.from || '—'} → {booking.trip?.to || '—'}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          🗓️ {booking.trip?.pickupDate || '—'} at {booking.trip?.pickupTime || '—'}
                        </div>
                        <div className="text-xs text-amber-700 font-medium">
                          {booking.trip?.tripType === 'roundtrip' ? 'Round Trip' : 'One Way'}
                          {booking.trip?.distance ? ` • ${booking.trip.distance} km` : ''}
                        </div>
                      </td>

                      {/* Vehicle */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {booking.vehicle?.name || 'Cab'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {booking.vehicle?.type || `${booking.vehicle?.seaters || 4} Seater`}
                        </div>
                      </td>

                      {/* Status & Change Dropdown */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <select
                            value={(booking.status || 'confirmed').toLowerCase()}
                            disabled={updatingId === booking.id}
                            onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                            className={`text-xs font-semibold rounded-lg px-2.5 py-1.5 border shadow-sm outline-none cursor-pointer transition ${getStatusColor(booking.status)} ${
                              updatingId === booking.id ? 'opacity-50 cursor-wait' : ''
                            }`}
                          >
                            <option value="confirmed">🟢 Confirmed</option>
                            <option value="in-progress">🟣 In Progress</option>
                            <option value="completed">🔵 Completed</option>
                            <option value="cancelled">🔴 Cancelled</option>
                            <option value="pending">🟡 Pending</option>
                          </select>
                          {updatingId === booking.id && (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-500 border-t-transparent" />
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSelectedBooking(booking)}
                            className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold transition flex items-center gap-1"
                          >
                            <FiEye /> View
                          </button>

                          {/* Super Admin only: Delete button */}
                          {isSuperAdmin && (
                            <button
                              onClick={() => deleteBooking(booking.id)}
                              className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-xs font-semibold transition flex items-center gap-1"
                              title="Super Admin Only: Delete booking permanently"
                            >
                              <FiTrash2 /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredBookings.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-500 text-base font-medium">No bookings found matching your search / filter.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100 animate-slideUp">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <div>
                <h2 className="text-xl font-bold text-gray-900 font-syne">
                  Booking Details
                </h2>
                <p className="text-xs text-orange-600 font-mono font-semibold">
                  {selectedBooking.bookingId || selectedBooking.id}
                </p>
              </div>
              <button
                onClick={() => setSelectedBooking(null)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center font-bold text-lg transition"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Quick Status Control Inside Modal */}
              <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase font-bold text-orange-800 tracking-wider">Current Status</p>
                    <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-bold uppercase ${getStatusColor(selectedBooking.status)}`}>
                      {selectedBooking.status || 'CONFIRMED'}
                    </span>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-700 block mb-1">Change Status:</label>
                    <select
                      value={(selectedBooking.status || 'confirmed').toLowerCase()}
                      disabled={updatingId === selectedBooking.id}
                      onChange={(e) => updateBookingStatus(selectedBooking.id, e.target.value)}
                      className="px-3 py-2 bg-white border border-orange-300 rounded-xl text-sm font-semibold text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="confirmed">🟢 Confirmed</option>
                      <option value="in-progress">🟣 In Progress</option>
                      <option value="completed">🔵 Completed</option>
                      <option value="cancelled">🔴 Cancelled</option>
                      <option value="pending">🟡 Pending</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <FiUser className="text-orange-500" /> Customer Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Full Name</p>
                    <p className="font-semibold text-gray-800 mt-0.5">{selectedBooking.customer?.fullName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Mobile Number</p>
                    <p className="font-semibold text-gray-800 mt-0.5">{selectedBooking.customer?.mobile || 'N/A'}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-xs text-gray-500 uppercase font-semibold">Email Address</p>
                    <p className="font-semibold text-gray-800 mt-0.5">{selectedBooking.customer?.email || 'N/A'}</p>
                  </div>
                  {selectedBooking.customer?.gstin && (
                    <div className="sm:col-span-2">
                      <p className="text-xs text-gray-500 uppercase font-semibold">GSTIN</p>
                      <p className="font-semibold text-gray-800 mt-0.5">{selectedBooking.customer.gstin}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Trip Information */}
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                  🗺️ Trip Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">From Location</p>
                    <p className="font-semibold text-gray-800 mt-0.5">{selectedBooking.trip?.from || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">To Location</p>
                    <p className="font-semibold text-gray-800 mt-0.5">{selectedBooking.trip?.to || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Pickup Date & Time</p>
                    <p className="font-semibold text-gray-800 mt-0.5">
                      {selectedBooking.trip?.pickupDate} at {selectedBooking.trip?.pickupTime}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Trip Type</p>
                    <p className="font-semibold text-gray-800 mt-0.5">
                      {selectedBooking.trip?.tripType === 'roundtrip' ? 'Round Trip' : 'One Way'}
                      {selectedBooking.trip?.distance ? ` (${selectedBooking.trip.distance} km)` : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Vehicle Information */}
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                  🚗 Vehicle Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Vehicle</p>
                    <p className="font-semibold text-gray-800 mt-0.5">{selectedBooking.vehicle?.name || 'Cab'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Type / Capacity</p>
                    <p className="font-semibold text-gray-800 mt-0.5">
                      {selectedBooking.vehicle?.type || `${selectedBooking.vehicle?.seaters || 4} Seater`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pickup Instructions */}
              {selectedBooking.customer?.pickupInstructions && (
                <div className="bg-amber-50 rounded-2xl p-5 border border-amber-200">
                  <h3 className="text-sm font-bold text-amber-900 uppercase tracking-wider mb-1">
                    📝 Special Pickup Instructions
                  </h3>
                  <p className="text-sm text-amber-800 mt-1">
                    {selectedBooking.customer.pickupInstructions}
                  </p>
                </div>
              )}

              {/* Super Admin Delete Option inside Modal */}
              {isSuperAdmin && (
                <div className="pt-4 border-t border-gray-200 flex justify-end">
                  <button
                    onClick={() => deleteBooking(selectedBooking.id)}
                    className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold transition flex items-center gap-2 shadow-sm"
                  >
                    <FiTrash2 /> Delete This Booking
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;